'use client'

import { useState } from 'react'
import Link from 'next/link'
import Image from 'next/image'
import { FOOTER_LINKS, SOCIAL_LINKS, BOOK_A_DEMO_URL } from '@/lib/constants'
import { getStoredAttribution } from '@/lib/attribution'
import { ArrowUp } from 'lucide-react'

const SOCIAL_ICON_MAP: Record<string, React.ReactNode> = {
  linkedin: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>,
  instagram: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/></svg>,
  facebook: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>,
  youtube: <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 17a24.12 24.12 0 0 1 0-10 2 2 0 0 1 1.4-1.4 49.56 49.56 0 0 1 16.2 0A2 2 0 0 1 21.5 7a24.12 24.12 0 0 1 0 10 2 2 0 0 1-1.4 1.4 49.55 49.55 0 0 1-16.2 0A2 2 0 0 1 2.5 17"/><path d="m10 15 5-3-5-3z"/></svg>,
  tiktok: <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-2.88 2.5 2.89 2.89 0 0 1-2.89-2.89 2.89 2.89 0 0 1 2.89-2.89c.28 0 .54.04.79.1v-3.5a6.37 6.37 0 0 0-.79-.05A6.34 6.34 0 0 0 3.15 15a6.34 6.34 0 0 0 6.34 6.34 6.34 6.34 0 0 0 6.34-6.34V8.7a8.16 8.16 0 0 0 4.76 1.52v-3.4a4.85 4.85 0 0 1-1-.13z"/></svg>,
}

function MailingListSignup() {
  const [email, setEmail] = useState('')
  const [status, setStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle')

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setStatus('loading')
    const params = new URLSearchParams()
    params.append('form-name', 'mailing-list-signup')
    params.append('email', email)

    const ghlPayload = {
      formType: 'newsletter' as const,
      email,
      attribution: getStoredAttribution({ pageFamily: 'Footer' }),
    }

    Promise.allSettled([
      fetch('/__forms.html', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: params.toString(),
      }),
      fetch('/api/ghl/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(ghlPayload),
      }),
    ]).then(([netlifyResult]) => {
      if (netlifyResult.status === 'fulfilled' && (netlifyResult.value as Response).ok) {
        setStatus('success')
        setEmail('')
      } else {
        setStatus('error')
      }
    })
  }

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
      <div>
        <p className="text-sm font-semibold text-white">Get contractor insights in your inbox</p>
        <p className="text-xs text-zinc-400 mt-0.5">Tips, updates, and roofing industry news. No spam.</p>
      </div>
      {status === 'success' ? (
        <p className="text-sm text-emerald-400 font-medium">✓ You're on the list!</p>
      ) : (
        <form
          name="mailing-list-signup"
          data-netlify="true"
          onSubmit={handleSubmit}
          className="flex items-center gap-2 w-full sm:w-auto"
        >
          <input type="hidden" name="form-name" value="mailing-list-signup" />
          <input
            type="email"
            name="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="your@email.com"
            className="flex-1 sm:w-56 px-4 py-2.5 bg-zinc-900 border border-zinc-800 rounded-lg text-sm text-white placeholder-zinc-600 focus:outline-none focus:border-red-600/50 transition-colors"
          />
          <button
            type="submit"
            disabled={status === 'loading'}
            className="px-4 py-2.5 bg-red-600 hover:bg-red-700 text-white text-sm font-semibold rounded-lg transition-colors disabled:opacity-50 whitespace-nowrap"
          >
            {status === 'loading' ? '…' : 'Subscribe'}
          </button>
        </form>
      )}
    </div>
  )
}

function FooterLinkGroup({ title, links }: { title: string; links: readonly { label: string; href: string }[] }) {
  return (
    <div>
      <p className="text-xs font-semibold text-white uppercase tracking-wider mb-4">{title}</p>
      <ul className="space-y-0.5">
        {links.map((link) => (
          <li key={link.href}>
            <Link
              href={link.href}
              className="inline-block py-2 text-sm text-zinc-400 hover:text-white transition-colors duration-200"
            >
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </div>
  )
}

export function Footer() {

  function scrollToTop() {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  return (
    <footer className="border-t border-zinc-900">
      {/* Links Grid */}
      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-8 lg:gap-10">
            {/* Brand Column */}
            <div className="col-span-2 sm:col-span-3 lg:col-span-1 mb-4 lg:mb-0">
              <Link href="/">
                <Image
                  src="/assets/images/nav-logo-file.webp"
                  alt="BuilderLync"
                  width={160}
                  height={32}
                  style={{ height: '32px', width: 'auto' }}
                  className="mb-4"
                />
              </Link>
              <p className="text-sm text-zinc-400 leading-relaxed mb-4">
                The all-in-one operating system built for contractors.
              </p>
              <div className="flex items-center gap-2">
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.href}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="w-10 h-10 rounded-lg bg-zinc-900 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 transition-all duration-200"
                  >
                    {SOCIAL_ICON_MAP[social.icon] || <span className="text-xs font-bold">{social.label[0]}</span>}
                  </a>
                ))}
              </div>
            </div>

            <FooterLinkGroup title="Product" links={FOOTER_LINKS.product} />
            <FooterLinkGroup title="Solutions" links={FOOTER_LINKS.solutions} />
            {FOOTER_LINKS.compare.length > 0 && (
              <FooterLinkGroup title="Compare" links={FOOTER_LINKS.compare} />
            )}
            <FooterLinkGroup title="Company" links={FOOTER_LINKS.company} />
            <FooterLinkGroup title="Legal" links={FOOTER_LINKS.legal} />
          </div>
        </div>
      </div>

      {/* Mailing List Signup */}
      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
          <MailingListSignup />
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-zinc-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-zinc-400">
            &copy; {new Date().getFullYear()} BuilderLync Inc. All rights reserved.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-x-2 text-xs text-zinc-400">
            <span>Jacksonville, FL</span>
            <span>·</span>
            <a href="tel:+18662513315" className="inline-block py-2.5 hover:text-white transition-colors">(866) 251-3315</a>
            <span>·</span>
            <a href="mailto:info@builderlync.com" className="inline-block py-2.5 hover:text-white transition-colors">info@builderlync.com</a>
          </div>
        </div>
      </div>

      {/* Back to top */}
      <button
        onClick={scrollToTop}
        className="fixed bottom-20 right-4 sm:bottom-6 sm:right-6 z-40 w-11 h-11 rounded-full bg-zinc-900 border border-zinc-800 flex items-center justify-center text-zinc-400 hover:text-white hover:bg-zinc-800 hover:border-zinc-700 transition-all shadow-lg"
        aria-label="Back to top"
      >
        <ArrowUp size={16} />
      </button>
    </footer>
  )
}
